// The firm cockpit's global focus-ratio cell: one client's reading of whichever ratio the firm has
// focused the roster on — a 12-month sparkline, the end value, the change from the prior month, and
// the benchmark when the ratio has one.
//
// Benchmark treatment is deliberately quiet. A roster row already carries a name, a grade, a stage
// and two other numbers, so the benchmark gets exactly two marks: a faint dashed reference line in
// the sparkline and a muted "vs <value>" under the reading. The source (IRS / own average / band)
// lives in the tooltip rather than a third visible chip.

import React from 'react'
import type { FocusRatioSlice } from '../../../data/ServerApiClient'
import { formatRatioValue } from './cfoFormat'
import {
  ratioBenchmarkAnchor,
  ratioBenchmarkSource,
  ratioNoBenchmarkNote,
} from './cfoRatioBenchmarking'
import { Sparkline } from './charts'

const SPARK_W = 76
const SPARK_H = 26

export type CockpitFocusRatioCellProps = {
  /** The focused ratio for this client, or null when the client's pack has no such entry. */
  slice: FocusRatioSlice | null | undefined
  /** True once this client has been analyzed at all — distinguishes "no pack yet" from "not tracked". */
  analyzed: boolean
}

/**
 * Whether a move in this ratio is an improvement. Polarity, never raw sign: a falling DSO is good
 * news and a falling current ratio is not. `null` polarity (DPO, A/P turnover) has no better
 * direction, so the caller renders those deltas without a good/bad tone.
 */
export function focusDeltaIsGood(
  delta: number,
  higherIsBetter: boolean | null,
): boolean | null {
  if (higherIsBetter == null) return null
  if (delta === 0) return null
  return higherIsBetter ? delta > 0 : delta < 0
}

/** Latest minus the immediately prior calendar month. Missing slots never collapse. */
export function calendarMonthOverMonthDelta(
  trend: Array<{ period: string; value: number | null }>,
): number | null {
  if (trend.length < 2) return null
  const [prior, latest] = trend.slice(-2)
  if (prior.value == null || latest.value == null || !Number.isFinite(prior.value) || !Number.isFinite(latest.value)) return null
  const month = (period: string): number | null => {
    const match = /^(\d{4})-(0[1-9]|1[0-2])(?:-\d{2})?$/.exec(period)
    return match ? Number(match[1]) * 12 + Number(match[2]) - 1 : null
  }
  const before = month(prior.period)
  const after = month(latest.period)
  return before != null && after != null && after - before === 1 ? latest.value - prior.value : null
}

export function focusMonthOverMonthDelta(slice: FocusRatioSlice): number | null {
  if (slice.latest == null || !Number.isFinite(slice.latest)) return null
  return calendarMonthOverMonthDelta(slice.trend || [])
}

/**
 * Tooltip label for one trend point. Carries the year, unlike the Ratios page's `formatShortMonth`:
 * a 12-month window straddles a year boundary, and "Jan" alone is ambiguous there.
 */
export function formatFocusPeriod(period: string | null | undefined): string {
  if (!period) return ''
  const parsed = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return String(period).slice(0, 7)
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Format a delta in the ratio's own unit — percentage points for fractions, days for days. */
export function formatFocusDelta(delta: number, unit: FocusRatioSlice['unit']): string {
  const abs = Math.abs(delta)
  switch (unit) {
    case 'ratio':
      return `${(abs * 100).toFixed(1)} pts`
    case 'days':
      return `${abs.toFixed(0)}d`
    case 'times':
      return abs.toFixed(2)
    default:
      return formatRatioValue(abs, unit)
  }
}

export function CockpitFocusRatioCell({ slice, analyzed }: CockpitFocusRatioCellProps) {
  if (!slice) {
    return (
      <div className="r2-focus">
        <span className="r2-focus-empty">{analyzed ? 'Not tracked' : 'Not analyzed yet'}</span>
      </div>
    )
  }

  // Retain the calendar slots. A sparse series is unavailable for this compact
  // renderer; filtering would join missing months and relabel its tooltips.
  const points = slice.trend || []
  const complete = points.every((p) => p?.value != null && Number.isFinite(p.value))
  const sparse = !complete
  const values = points.map((p) => p.value)
  const value = slice.latest
  const delta = focusMonthOverMonthDelta(slice)
  const good = delta != null ? focusDeltaIsGood(delta, slice.higher_is_better) : null

  const anchor = ratioBenchmarkAnchor(slice)
  const source = ratioBenchmarkSource(slice)
  const title =
    anchor != null && source
      ? `${slice.label} · benchmark ${formatRatioValue(anchor, slice.unit)} (${source.tag})`
      : `${slice.label} · no benchmark — ${ratioNoBenchmarkNote(slice)}`

  // Shares the roster-wide pill so the legend at the top of the cockpit explains all three trend
  // columns at once.
  const deltaClass = `delta-pill ${good == null ? 'neutral' : good ? 'up' : 'down'}`

  return (
    <div className="r2-focus">
      {complete && points.length >= 2 ? (
        <Sparkline
          className="r2-focus-spark"
          values={values as number[]}
          // Tone the line by polarity of the month's move, not by window slope, so it agrees
          // with the arrow sitting next to it.
          down={good === false}
          neutral={good == null}
          benchmark={anchor}
          endDot
          interactive
          labels={points.map((p) => formatFocusPeriod(p.period))}
          formatValue={(v) => formatRatioValue(v, slice.unit)}
          width={SPARK_W}
          height={SPARK_H}
          ariaLabel={`${slice.label} trend, ${points.length} months`}
        />
      ) : (
        <span className="r2-focus-spark-gap" aria-hidden={!sparse} aria-label={sparse ? "Trend unavailable: some calendar months have no value." : undefined} title={sparse ? "Trend unavailable: some calendar months have no value." : undefined} />
      )}
      {/* The benchmark title lives on the reading, not the whole cell: over the sparkline it would
          race the hover tooltip and the browser's native bubble would win. */}
      <div className="r2-focus-read" title={title}>
        <div className="r2-focus-top">
          <span className="r2-focus-val">{formatRatioValue(value, slice.unit)}</span>
          {delta != null && delta !== 0 && (
            <span className={deltaClass}>
              {delta > 0 ? '▲' : '▼'} {formatFocusDelta(delta, slice.unit)}
            </span>
          )}
        </div>
        {anchor != null ? (
          <span className="r2-focus-vs">vs {formatRatioValue(anchor, slice.unit)}</span>
        ) : points.length < 2 ? (
          <span className="r2-focus-vs">Needs 2+ months</span>
        ) : null}
      </div>
    </div>
  )
}

export default CockpitFocusRatioCell
